import { useEffect } from "react";
import { Calendar, CheckCircle } from "lucide-react";

const HUBSPOT_SCRIPT_URL = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
const HUBSPOT_MEETINGS_URL = "https://meetings.hubspot.com/freelanceurs/new-home-call";

export default function RendezVousPage() {
  useEffect(() => {
    // Load Hubspot Meetings Embed Script
    const script = document.createElement("script");
    script.src = HUBSPOT_SCRIPT_URL;
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector(
        `script[src="${HUBSPOT_SCRIPT_URL}"]`
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="overflow-hidden" data-testid="rendez-vous-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-white" data-testid="rdv-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in">
              <Calendar className="h-4 w-4" />
              Prise de rendez-vous
            </div>
            <h1 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-6 animate-fade-in-up">
              Réservez votre{" "}
              <span className="gradient-text">appel découverte</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed animate-fade-in-up delay-200">
              Discutons de votre situation et découvrez comment FREELANCEURS.io 
              peut vous aider à gagner plus et payer moins.
            </p>
          </div>

          {/* Benefits */}
          <div className="flex flex-wrap justify-center gap-6 mb-12 animate-fade-in-up delay-300">
            {[
              "Appel gratuit de 30 min",
              "Analyse personnalisée",
              "Sans engagement",
            ].map((benefit, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-full"
              >
                <CheckCircle className="h-4 w-4 text-[#52fbb0]" />
                <span className="text-slate-700 text-sm font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hubspot Embed Section */}
      <section className="py-8 md:py-12 bg-slate-50" data-testid="hubspot-embed">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden min-h-[700px]">
            {/* Hubspot Meetings Embed */}
            <div
              className="meetings-iframe-container"
              data-src={`${HUBSPOT_MEETINGS_URL}?embed=true`}
              style={{ minHeight: "700px" }}
            />
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-manrope font-bold text-2xl md:text-3xl text-slate-900 mb-6">
              Pourquoi prendre rendez-vous ?
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mt-10">
              {[
                {
                  title: "Audit gratuit",
                  description: "Analyse complète de votre situation actuelle et de vos objectifs",
                },
                {
                  title: "Conseils personnalisés",
                  description: "Recommandations sur le statut optimal et les optimisations possibles",
                },
                {
                  title: "Plan d'action",
                  description: "Feuille de route claire pour maximiser vos revenus",
                },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-[#52fbb0]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <span className="font-manrope font-bold text-[#52fbb0] text-lg">
                      {index + 1}
                    </span>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
